import React, { useState, useEffect } from "react";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import axios from "axios";
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  Save,
  Copy,
  Download,
  Building2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileSpreadsheet
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  { key: "jan", label: "Jan" },
  { key: "feb", label: "Feb" },
  { key: "march", label: "March" },
  { key: "april", label: "April" },
  { key: "may", label: "May" },
  { key: "june", label: "June" },
  { key: "july", label: "July" },
  { key: "aug", label: "Aug" },
  { key: "sept", label: "Sept" },
  { key: "oct", label: "Oct" },
  { key: "nov", label: "Nov" },
  { key: "dec", label: "Dec" },
];

export default function AdminBudgetPlanning() {
  const { toast } = useToast();
  const [budgets, setBudgets] = useState([]);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showNewBudgetDialog, setShowNewBudgetDialog] = useState(false);
  const [showNewCategoryDialog, setShowNewCategoryDialog] = useState(false);
  const [showNewLineDialog, setShowNewLineDialog] = useState(false);
  const [selectedCategoryForLine, setSelectedCategoryForLine] = useState(null);
  const [viewMode, setViewMode] = useState("combined"); // "combined", "client", "holding", "allocation"

  // New budget form
  const [newBudget, setNewBudget] = useState({
    name: "",
    year: new Date().getFullYear(),
    company_type: "combined"
  });

  // New category form
  const [newCategory, setNewCategory] = useState({
    name: "",
    type: "income"
  });

  // New line item form
  const [newLineItem, setNewLineItem] = useState({
    line_code: "",
    name: "",
    allocation_client: 100,
    allocation_holding: 0
  });

  useEffect(() => {
    fetchBudgets();
  }, []);

  const fetchBudgets = async () => {
    try {
      const url = `${API}/api/admin/budgets`;
      
      // Use global axios interceptor - no manual token needed
      const res = await axios.get(url);
      
      
      setBudgets(res.data);
      if (res.data.length > 0) {
        setSelectedBudget(res.data[0]);
      }
    } catch (err) {
      console.error("❌ ERROR fetching budgets");
      console.error("Error:", err);
      console.error("Response status:", err.response?.status);
      console.error("Response data:", err.response?.data);
      
      // Show alert for debugging
      if (err.response?.status === 401) {
        alert("Authentication Error: Your session may have expired. Please logout and login again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBudget = async () => {
    
    if (!newBudget.name.trim()) {
      toast({ title: "Error", description: "Budget name is required", variant: "destructive" });
      return;
    }

    
    try {
      const requestData = {
        name: newBudget.name,
        year: parseInt(newBudget.year),
        company_type: newBudget.company_type,
        categories: []
      };
      
      
      // Use global axios interceptor - no manual token needed
      const res = await axios.post(`${API}/api/admin/budgets`, requestData);
      
      toast({ title: "✅ Success!", description: "Budget created successfully" });
      setShowNewBudgetDialog(false);
      setNewBudget({ name: "", year: new Date().getFullYear(), company_type: "combined" });
      await fetchBudgets();
      setSelectedBudget(res.data);
    } catch (err) {
      console.error("=== ERROR creating budget ===");
      console.error("Error object:", err);
      console.error("Response:", err.response);
      console.error("Status:", err.response?.status);
      console.error("Data:", err.response?.data);
      
      const errorMsg = err.response?.data?.detail || err.message || "Failed to create budget";
      toast({ 
        title: "❌ Error", 
        description: errorMsg, 
        variant: "destructive" 
      });
      
      alert("Error creating budget: " + errorMsg);
    }
  };

  const handleAddCategory = () => {
    if (!newCategory.name.trim()) {
      toast({ title: "Error", description: "Category name is required", variant: "destructive" });
      return;
    }

    const categoryId = `cat_${Date.now()}`;
    const updatedCategories = [
      ...selectedBudget.categories,
      {
        category_id: categoryId,
        name: newCategory.name,
        type: newCategory.type,
        line_items: [],
        is_expanded: true
      }
    ];

    setSelectedBudget({ ...selectedBudget, categories: updatedCategories });
    setShowNewCategoryDialog(false);
    setNewCategory({ name: "", type: "income" });
  };

  const handleAddLineItem = () => {
    if (!newLineItem.name.trim() || !selectedCategoryForLine) {
      toast({ title: "Error", description: "Line name is required", variant: "destructive" });
      return;
    }

    const lineId = `line_${Date.now()}`;
    const emptyMonthlyValues = {};
    MONTHS.forEach(m => emptyMonthlyValues[m.key] = 0);

    const newLine = {
      line_id: lineId,
      line_code: newLineItem.line_code,
      name: newLineItem.name,
      monthly_values: emptyMonthlyValues,
      allocation_client: parseFloat(newLineItem.allocation_client),
      allocation_holding: parseFloat(newLineItem.allocation_holding),
      notes: ""
    };

    const updatedCategories = selectedBudget.categories.map(cat => {
      if (cat.category_id === selectedCategoryForLine) {
        return {
          ...cat,
          line_items: [...cat.line_items, newLine]
        };
      }
      return cat;
    });

    setSelectedBudget({ ...selectedBudget, categories: updatedCategories });
    setShowNewLineDialog(false);
    setNewLineItem({ line_code: "", name: "", allocation_client: 100, allocation_holding: 0 });
  };

  const handleUpdateCell = (categoryId, lineId, month, value) => {
    const updatedCategories = selectedBudget.categories.map(cat => {
      if (cat.category_id === categoryId) {
        const updatedLines = cat.line_items.map(line => {
          if (line.line_id === lineId) {
            return {
              ...line,
              monthly_values: {
                ...line.monthly_values,
                [month]: parseFloat(value) || 0
              }
            };
          }
          return line;
        });
        return { ...cat, line_items: updatedLines };
      }
      return cat;
    });

    setSelectedBudget({ ...selectedBudget, categories: updatedCategories });
  };

  const handleSaveBudget = async () => {
    if (!selectedBudget) return;

    setSaving(true);
    try {
      // Use global axios interceptor - no manual token needed
      await axios.put(`${API}/api/admin/budgets/${selectedBudget.budget_id}`, {
        categories: selectedBudget.categories
      });

      toast({ title: "Success", description: "Budget saved successfully" });
      fetchBudgets();
    } catch (err) {
      toast({ title: "Error", description: "Failed to save budget", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const calculateLineTotal = (monthlyValues) => {
    return Object.values(monthlyValues).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  };

  const calculateCategoryTotal = (category, month = null) => {
    if (month) {
      return category.line_items.reduce((sum, line) => 
        sum + (parseFloat(line.monthly_values[month]) || 0), 0
      );
    }
    return category.line_items.reduce((sum, line) => 
      sum + calculateLineTotal(line.monthly_values), 0
    );
  };

  const calculateMonthTotal = (month) => {
    let income = 0;
    let expenses = 0;

    selectedBudget?.categories.forEach(cat => {
      const monthTotal = calculateCategoryTotal(cat, month);
      if (cat.type === "income") income += monthTotal;
      else if (cat.type === "expense") expenses += monthTotal;
    });

    return { income, expenses, profit: income - expenses };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("no-NO", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const toggleCategory = (categoryId) => {
    const updatedCategories = selectedBudget.categories.map(cat => {
      if (cat.category_id === categoryId) {
        return { ...cat, is_expanded: !cat.is_expanded };
      }
      return cat;
    });
    setSelectedBudget({ ...selectedBudget, categories: updatedCategories });
  };

  const handleDeleteCategory = (categoryId) => {
    const updatedCategories = selectedBudget.categories.filter(
      cat => cat.category_id !== categoryId
    );
    setSelectedBudget({ ...selectedBudget, categories: updatedCategories });
  };

  const handleDeleteLine = (categoryId, lineId) => {
    const updatedCategories = selectedBudget.categories.map(cat => {
      if (cat.category_id === categoryId) {
        return {
          ...cat,
          line_items: cat.line_items.filter(line => line.line_id !== lineId)
        };
      }
      return cat;
    });
    setSelectedBudget({ ...selectedBudget, categories: updatedCategories });
  };

  if (loading) {
    return (
      <DashboardLayout navItems={adminNavItems} title="Budget Planning">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={adminNavItems} title="Budget Planning">
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white font-['Unbounded']">
              💰 Budget Planning & Cost Allocation
            </h1>
            <p className="text-[#94A3B8] mt-1">Manage budgets for Client Company & Holding Company</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => setShowNewBudgetDialog(true)}
              className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Budget
            </Button>
            {selectedBudget && (
              <Button
                onClick={handleSaveBudget}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save"}
              </Button>
            )}
          </div>
        </div>

        {/* Budget Selector & Summary */}
        {budgets.length > 0 && selectedBudget && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Budget Selector */}
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-sm">Select Budget</CardTitle>
              </CardHeader>
              <CardContent>
                <Select
                  value={selectedBudget.budget_id}
                  onValueChange={(value) => {
                    const budget = budgets.find(b => b.budget_id === value);
                    setSelectedBudget(budget);
                  }}
                >
                  <SelectTrigger className="bg-white/5 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {budgets.map((budget) => (
                      <SelectItem key={budget.budget_id} value={budget.budget_id}>
                        {budget.name} ({budget.year})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Summary Cards */}
            <Card className="bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-400 text-sm">Total Income</p>
                    <p className="text-2xl font-bold text-white mt-1">
                      {formatCurrency(selectedBudget.summary?.total_income)}
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-green-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-red-500/10 to-red-500/5 border-red-500/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-400 text-sm">Total Expenses</p>
                    <p className="text-2xl font-bold text-white mt-1">
                      {formatCurrency(selectedBudget.summary?.total_expenses)}
                    </p>
                  </div>
                  <TrendingDown className="w-8 h-8 text-red-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-[#23F7DD]/10 to-[#23F7DD]/5 border-[#23F7DD]/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#23F7DD] text-sm">Operating Profit</p>
                    <p className="text-2xl font-bold text-white mt-1">
                      {formatCurrency(selectedBudget.summary?.operating_profit)}
                    </p>
                  </div>
                  <DollarSign className="w-8 h-8 text-[#23F7DD]" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Budget Table */}
        {selectedBudget && (
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardHeader className="border-b border-white/10">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">
                  <FileSpreadsheet className="w-5 h-5 inline mr-2" />
                  {selectedBudget.name} - {selectedBudget.year}
                </CardTitle>
                <Button
                  onClick={() => setShowNewCategoryDialog(true)}
                  size="sm"
                  className="bg-white/10 hover:bg-white/20"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Category
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white/5 sticky top-0 z-10">
                    <tr>
                      <th className="text-left p-3 text-[#94A3B8] font-medium min-w-[250px]">
                        Line Item
                      </th>
                      {MONTHS.map((month) => (
                        <th key={month.key} className="text-right p-3 text-[#94A3B8] font-medium min-w-[100px]">
                          {month.label}
                        </th>
                      ))}
                      <th className="text-right p-3 text-[#23F7DD] font-bold min-w-[120px]">
                        Total
                      </th>
                      <th className="text-center p-3 text-[#94A3B8] font-medium w-[100px]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBudget.categories.map((category) => (
                      <React.Fragment key={category.category_id}>
                        {/* Category Header */}
                        <tr className="bg-white/5 border-t-2 border-white/20">
                          <td
                            className="p-3 cursor-pointer hover:bg-white/10"
                            onClick={() => toggleCategory(category.category_id)}
                          >
                            <div className="flex items-center gap-2">
                              {category.is_expanded ? (
                                <ChevronDown className="w-4 h-4 text-[#23F7DD]" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-[#23F7DD]" />
                              )}
                              <span className="font-bold text-white">
                                {category.name}
                              </span>
                              <Badge className={
                                category.type === "income" 
                                  ? "bg-green-500/20 text-green-400" 
                                  : "bg-red-500/20 text-red-400"
                              }>
                                {category.type}
                              </Badge>
                            </div>
                          </td>
                          {MONTHS.map((month) => (
                            <td key={month.key} className="text-right p-3 text-white font-bold">
                              {formatCurrency(calculateCategoryTotal(category, month.key))}
                            </td>
                          ))}
                          <td className="text-right p-3 text-[#23F7DD] font-bold">
                            {formatCurrency(calculateCategoryTotal(category))}
                          </td>
                          <td className="text-center p-3">
                            <div className="flex items-center justify-center gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSelectedCategoryForLine(category.category_id);
                                  setShowNewLineDialog(true);
                                }}
                                className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteCategory(category.category_id)}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>

                        {/* Line Items */}
                        {category.is_expanded && category.line_items.map((line) => (
                          <tr key={line.line_id} className="border-t border-white/5 hover:bg-white/5">
                            <td className="p-3 pl-12">
                              <div className="flex items-center gap-2">
                                {line.line_code && (
                                  <span className="text-[#94A3B8] text-xs">{line.line_code}</span>
                                )}
                                <span className="text-white">{line.name}</span>
                              </div>
                            </td>
                            {MONTHS.map((month) => (
                              <td key={month.key} className="p-1">
                                <Input
                                  type="number"
                                  value={line.monthly_values[month.key] || 0}
                                  onChange={(e) => handleUpdateCell(
                                    category.category_id,
                                    line.line_id,
                                    month.key,
                                    e.target.value
                                  )}
                                  className="bg-white/5 border-white/10 text-white text-right h-8 text-sm"
                                />
                              </td>
                            ))}
                            <td className="text-right p-3 text-white font-semibold">
                              {formatCurrency(calculateLineTotal(line.monthly_values))}
                            </td>
                            <td className="text-center p-3">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteLine(category.category_id, line.line_id)}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {budgets.length === 0 && (
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-12 text-center">
              <FileSpreadsheet className="w-16 h-16 mx-auto text-[#94A3B8] mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">No Budgets Yet</h3>
              <p className="text-[#94A3B8] mb-6">
                Create your first budget to start planning your finances
              </p>
              <Button
                onClick={() => setShowNewBudgetDialog(true)}
                className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Budget
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* New Budget Dialog */}
      <Dialog open={showNewBudgetDialog} onOpenChange={setShowNewBudgetDialog}>
        <DialogContent className="bg-[#1a1a2e] border-[#2a2a4a] text-white">
          <DialogHeader>
            <DialogTitle>Create New Budget</DialogTitle>
            <DialogDescription className="text-gray-400">
              Create a new annual budget for financial planning
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Budget Name</Label>
              <Input
                value={newBudget.name}
                onChange={(e) => setNewBudget({ ...newBudget, name: e.target.value })}
                placeholder="e.g., AllStillas Budget 2025"
                className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
              />
            </div>
            <div>
              <Label>Year</Label>
              <Input
                type="number"
                value={newBudget.year}
                onChange={(e) => setNewBudget({ ...newBudget, year: parseInt(e.target.value) })}
                className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
              />
            </div>
            <div>
              <Label>Company Type</Label>
              <Select
                value={newBudget.company_type}
                onValueChange={(value) => setNewBudget({ ...newBudget, company_type: value })}
              >
                <SelectTrigger className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="combined">Combined (Both Companies)</SelectItem>
                  <SelectItem value="client_company">Client Company Only</SelectItem>
                  <SelectItem value="holding_company">Holding Company Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNewBudgetDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                handleCreateBudget();
              }} 
              className="bg-[#23F7DD] text-[#050505]"
            >
              Create Budget
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Category Dialog */}
      <Dialog open={showNewCategoryDialog} onOpenChange={setShowNewCategoryDialog}>
        <DialogContent className="bg-[#1a1a2e] border-[#2a2a4a] text-white">
          <DialogHeader>
            <DialogTitle>Add Category</DialogTitle>
            <DialogDescription className="text-gray-400">
              Add a new category to organize line items
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Category Name</Label>
              <Input
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                placeholder="e.g., Sales Revenue"
                className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select
                value={newCategory.type}
                onValueChange={(value) => setNewCategory({ ...newCategory, type: value })}
              >
                <SelectTrigger className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNewCategoryDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddCategory} className="bg-[#23F7DD] text-[#050505]">
              Add Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Line Item Dialog */}
      <Dialog open={showNewLineDialog} onOpenChange={setShowNewLineDialog}>
        <DialogContent className="bg-[#1a1a2e] border-[#2a2a4a] text-white">
          <DialogHeader>
            <DialogTitle>Add Line Item</DialogTitle>
            <DialogDescription className="text-gray-400">
              Add a new line item with cost allocation
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Line Code (Optional)</Label>
              <Input
                value={newLineItem.line_code}
                onChange={(e) => setNewLineItem({ ...newLineItem, line_code: e.target.value })}
                placeholder="e.g., 4010"
                className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
              />
            </div>
            <div>
              <Label>Line Item Name</Label>
              <Input
                value={newLineItem.name}
                onChange={(e) => setNewLineItem({ ...newLineItem, name: e.target.value })}
                placeholder="e.g., Salgsint fra hovedprodukt"
                className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Client Company %</Label>
                <Input
                  type="number"
                  value={newLineItem.allocation_client}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    setNewLineItem({ 
                      ...newLineItem, 
                      allocation_client: val,
                      allocation_holding: 100 - val
                    });
                  }}
                  min="0"
                  max="100"
                  className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                />
              </div>
              <div>
                <Label>Holding Company %</Label>
                <Input
                  type="number"
                  value={newLineItem.allocation_holding}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    setNewLineItem({ 
                      ...newLineItem, 
                      allocation_holding: val,
                      allocation_client: 100 - val
                    });
                  }}
                  min="0"
                  max="100"
                  className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNewLineDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddLineItem} className="bg-[#23F7DD] text-[#050505]">
              Add Line Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
